import { type ComputedRef, computed, type ShallowRef, shallowRef } from 'vue';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { ERROR_FALLBACKS, useUiStore } from '@/app/stores/ui';
import { analytics } from '@/services/Analytics';
import { faceDetection } from '@/services/FaceDetection';
import { sentryService } from '@/services/telemetry/sentry';
import { track, warnMessageOf } from '@/shared/utils/track-telemetry';
import type {
  FaceDemographicField,
  FaceDemographicOption,
  FaceDropdownPanelStyle,
  FaceDropdownState,
  FaceSelectionFace,
  FaceValueChangeEvent,
} from './faceSelectionTypes';

interface FaceSelectionKioskConfig {
  demographics_confirmations?: string[] | null;
  multi_subject_enabled?: boolean | null;
  classic_gallery_layout?: string | null;
  show_nonbinary_gender?: boolean | null;
}

interface FaceSelectionStore {
  faces: FaceSelectionFace[];
  facesSelected: FaceSelectionFace[];
  kiosk?: FaceSelectionKioskConfig | null;
}

interface FaceSelectionAnalytics {
  METRICS: {
    demographics_changed: string;
    has_multiple_people: string;
    num_faces_detected: string;
    time_demographics_confirmed: string;
    time_face_selection_initiated: string;
    time_face_selection_success: string;
  };
  trackBoolean: (metric: string, options?: unknown) => unknown;
  trackData: (data: Record<string, unknown>) => unknown;
  trackTimeTo: (metric: string) => unknown;
}

interface FaceSelectionDetection {
  changeSelectedFaces: (options: { set?: string[]; toggle?: string }) => void;
  isFaceSelected: (faceUuid: string) => boolean;
}

interface FaceSelectionRouter {
  redirectToStep: (stepId: string) => unknown;
}

export interface UseFaceSelectionFlowOptions {
  cancelPendingScroll: () => void;
  translate: (key: string) => string;
  updateScrollArrowVisibility: () => void;
}

export interface FaceSelectionFlow {
  ageOptions: ComputedRef<FaceDemographicOption[]>;
  closeDropdown: () => void;
  demographicsConfirmations: ComputedRef<string[]>;
  dropdownOptions: ComputedRef<FaceDemographicOption[]>;
  dropdownPanelStyle: ComputedRef<FaceDropdownPanelStyle>;
  dropdownState: ShallowRef<FaceDropdownState | null>;
  enter: () => void;
  genderOptions: ComputedRef<FaceDemographicOption[]>;
  getDropdownLabel: (
    face: FaceSelectionFace,
    field: FaceDemographicField
  ) => string;
  isConfirming: ShallowRef<boolean>;
  isFaceSelected: (face: FaceSelectionFace) => boolean;
  isMounted: ShallowRef<boolean>;
  leave: () => void;
  onAgeChange: (face: FaceSelectionFace, event: FaceValueChangeEvent) => void;
  onConfirmClick: () => Promise<void>;
  onFaceClick: (face: FaceSelectionFace) => void;
  onGenderChange: (
    face: FaceSelectionFace,
    event: FaceValueChangeEvent
  ) => void;
  onRaceChange: (face: FaceSelectionFace, event: FaceValueChangeEvent) => void;
  openDropdown: (
    face: FaceSelectionFace,
    field: FaceDemographicField,
    event: MouseEvent
  ) => void;
  raceOptions: ComputedRef<FaceDemographicOption[]>;
  selectDropdownOption: (value: string) => void;
  showAgeSelector: ComputedRef<boolean>;
  showGenderSelector: ComputedRef<boolean>;
  showNonBinaryGender: ComputedRef<boolean>;
  showRaceSelector: ComputedRef<boolean>;
}

const DEMOGRAPHIC_LABELS: Record<FaceDemographicField, string> = {
  gender: 'gender',
  age_group: 'age group',
  race: 'race',
};

export function useFaceSelectionFlow({
  cancelPendingScroll,
  translate,
  updateScrollArrowVisibility,
}: UseFaceSelectionFlowOptions): FaceSelectionFlow {
  const faceStore = store as unknown as FaceSelectionStore;
  const faceAnalytics = analytics as unknown as FaceSelectionAnalytics;
  const selection = faceDetection as unknown as FaceSelectionDetection;
  const faceRouter = router as unknown as FaceSelectionRouter;
  const ui = useUiStore();

  const isMounted = shallowRef(true);
  const isConfirming = shallowRef(false);
  const dropdownState = shallowRef<FaceDropdownState | null>(null);

  const kioskConfig = (): FaceSelectionKioskConfig | null | undefined =>
    faceStore.kiosk;

  const demographicsConfirmations = computed(
    (): string[] => kioskConfig()?.demographics_confirmations || []
  );
  const showGenderSelector = computed((): boolean =>
    demographicsConfirmations.value.includes('gender')
  );
  const showAgeSelector = computed((): boolean =>
    demographicsConfirmations.value.includes('age')
  );
  const showRaceSelector = computed((): boolean =>
    demographicsConfirmations.value.includes('race')
  );
  const showNonBinaryGender = computed(
    (): boolean => kioskConfig()?.show_nonbinary_gender === true
  );

  const demographicOption = (
    value: string,
    labelKey: string
  ): FaceDemographicOption => ({
    value,
    label: translate(`FacesIndividual.${labelKey}`),
  });
  const genderOptions = computed<FaceDemographicOption[]>(() => {
    const options = [
      demographicOption('male', 'genderMale'),
      demographicOption('female', 'genderFemale'),
    ];
    if (showNonBinaryGender.value) {
      options.push(demographicOption('non-binary', 'genderNonBinary'));
    }
    return options;
  });
  const ageOptions = computed<FaceDemographicOption[]>(() => [
    demographicOption('0-2', 'age0to2'),
    demographicOption('3-9', 'age3to9'),
    demographicOption('10-19', 'age10to19'),
    demographicOption('20-29', 'age20to29'),
    demographicOption('30-39', 'age30to39'),
    demographicOption('40-49', 'age40to49'),
    demographicOption('50-59', 'age50to59'),
    demographicOption('60-69', 'age60to69'),
    demographicOption('70+', 'age70plus'),
  ]);
  const raceOptions = computed<FaceDemographicOption[]>(() => [
    demographicOption('white', 'raceWhite'),
    demographicOption('black', 'raceBlack'),
    demographicOption('east_asian', 'raceEastAsian'),
    demographicOption('southeast_asian', 'raceSoutheastAsian'),
    demographicOption('indian', 'raceIndian'),
    demographicOption('latino', 'raceLatino'),
    demographicOption('mena', 'raceMena'),
  ]);

  function optionsForField(
    field: FaceDemographicField
  ): FaceDemographicOption[] {
    const options: Record<FaceDemographicField, FaceDemographicOption[]> = {
      gender: genderOptions.value,
      age_group: ageOptions.value,
      race: raceOptions.value,
    };
    return options[field] || [];
  }

  const dropdownOptions = computed((): FaceDemographicOption[] => {
    if (!dropdownState.value) {
      return [];
    }
    return optionsForField(dropdownState.value.field);
  });

  const dropdownPanelStyle = computed((): FaceDropdownPanelStyle => {
    if (!dropdownState.value) {
      return {};
    }

    const { rect } = dropdownState.value;
    // The panel is as wide as its trigger, thus a long label wraps to a
    // second line. The fit test allows half a line for each option, so a
    // wrapped list goes above the trigger instead of past the screen edge.
    const estimatedHeight = dropdownOptions.value.length * 63;
    const viewportHeight = window.innerHeight;
    const fitsBelow = rect.bottom + 4 + estimatedHeight <= viewportHeight;
    const style: FaceDropdownPanelStyle = {
      position: 'fixed',
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: 99999,
    };
    // estimatedHeight assumes one line per option, thus a label that wraps
    // makes the panel taller than the check above expects. The height is
    // bound to the room that is really there, so the panel scrolls at the
    // screen edge instead of running past it.
    if (fitsBelow) {
      style.top = `${rect.bottom + 4}px`;
      style.maxHeight = `${viewportHeight - rect.bottom - 12}px`;
    } else {
      style.bottom = `${viewportHeight - rect.top + 4}px`;
      style.maxHeight = `${rect.top - 12}px`;
    }
    return style;
  });

  function enter(): void {
    track(
      '[Sentry] addBreadcrumb failed:',
      () => {
        sentryService.addBreadcrumb(
          'navigation',
          'Face selection step mounted',
          {
            total_faces_detected: faceStore.faces.length,
            demographics_confirmations:
              kioskConfig()?.demographics_confirmations,
            show_nonbinary_gender: kioskConfig()?.show_nonbinary_gender,
          }
        );
      },
      warnMessageOf
    );

    if (faceStore.faces.length === 0) {
      // biome-ignore lint/suspicious/noConsole: preserve kiosk support diagnostics.
      console.warn('No faces detected, redirecting to camera');
      track(
        '[Sentry] addBreadcrumb failed:',
        () => {
          sentryService.addBreadcrumb(
            'error',
            'No faces detected - redirecting to camera',
            {},
            'warning'
          );
        },
        warnMessageOf
      );
      ui.showError(
        'face_detection_no_faces',
        null,
        ERROR_FALLBACKS.RETRY_CAMERA,
        { sentryLevel: 'warning', sentryCapture: false }
      );
      return;
    }

    faceAnalytics.trackTimeTo(
      faceAnalytics.METRICS.time_face_selection_initiated
    );
    faceAnalytics.trackData({
      [faceAnalytics.METRICS.num_faces_detected]: faceStore.faces.length,
    });

    track(
      '[Sentry] setContext failed:',
      () => {
        sentryService.setContext('face_selection', {
          total_faces_detected: faceStore.faces.length,
          faces_selected: 0,
        });
      },
      warnMessageOf
    );

    const firstFace = faceStore.faces[0];
    if (firstFace) {
      selection.changeSelectedFaces({ set: [firstFace.uuid] });
      track(
        '[Sentry] addBreadcrumb failed:',
        () => {
          sentryService.addBreadcrumb('user', 'Auto-selected largest face', {
            face_uuid: firstFace.uuid,
          });
        },
        warnMessageOf
      );
    }

    updateScrollArrowVisibility();
  }

  function onFaceClick(face: FaceSelectionFace): void {
    const wasSelected = selection.isFaceSelected(face.uuid);
    selection.changeSelectedFaces({ toggle: face.uuid });

    if (!(wasSelected || selection.isFaceSelected(face.uuid))) {
      track(
        '[Sentry] addBreadcrumb failed:',
        () => {
          sentryService.addBreadcrumb('user', 'Face selection limit reached', {
            face_uuid: face.uuid,
            total_selected: faceStore.facesSelected.length,
          });
        },
        warnMessageOf
      );
      return;
    }

    track(
      '[Sentry] face selection tracking failed:',
      () => {
        sentryService.addBreadcrumb(
          'user',
          wasSelected ? 'Face deselected' : 'Face selected',
          {
            face_uuid: face.uuid,
            total_selected: faceStore.facesSelected.length,
          }
        );
        sentryService.setContext('face_selection', {
          total_faces_detected: faceStore.faces.length,
          faces_selected: faceStore.facesSelected.length,
        });
      },
      warnMessageOf
    );
  }

  function isFaceSelected(face: FaceSelectionFace): boolean {
    return selection.isFaceSelected(face.uuid);
  }

  function onDemographicChange(
    face: FaceSelectionFace,
    field: FaceDemographicField,
    event: FaceValueChangeEvent
  ): void {
    const newValue = event.target.value;

    if (face[field] !== newValue) {
      faceAnalytics.trackBoolean(faceAnalytics.METRICS.demographics_changed);
      track(
        '[Sentry] addBreadcrumb failed:',
        () => {
          sentryService.addBreadcrumb(
            'user',
            `Demographics updated: ${DEMOGRAPHIC_LABELS[field]}`,
            { face_uuid: face.uuid }
          );
        },
        warnMessageOf
      );
    }

    face[field] = newValue;
  }

  function onGenderChange(
    face: FaceSelectionFace,
    event: FaceValueChangeEvent
  ): void {
    onDemographicChange(face, 'gender', event);
  }

  function onAgeChange(
    face: FaceSelectionFace,
    event: FaceValueChangeEvent
  ): void {
    onDemographicChange(face, 'age_group', event);
  }

  function onRaceChange(
    face: FaceSelectionFace,
    event: FaceValueChangeEvent
  ): void {
    onDemographicChange(face, 'race', event);
  }

  function getDropdownLabel(
    face: FaceSelectionFace,
    field: FaceDemographicField
  ): string {
    const value = face[field];
    const option = optionsForField(field).find(
      (candidate) => candidate.value === value
    );
    return option ? option.label : value || '\u2014';
  }

  function openDropdown(
    face: FaceSelectionFace,
    field: FaceDemographicField,
    event: MouseEvent
  ): void {
    if (
      dropdownState.value &&
      dropdownState.value.face === face &&
      dropdownState.value.field === field
    ) {
      dropdownState.value = null;
      return;
    }

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    dropdownState.value = {
      face,
      field,
      currentValue: face[field],
      rect,
    };
  }

  function closeDropdown(): void {
    dropdownState.value = null;
  }

  function selectDropdownOption(value: string): void {
    if (!dropdownState.value) {
      return;
    }

    const { face, field } = dropdownState.value;
    if (Object.hasOwn(DEMOGRAPHIC_LABELS, field)) {
      onDemographicChange(face, field, { target: { value } });
    }
    dropdownState.value = null;
  }

  async function onConfirmClick(): Promise<void> {
    if (isConfirming.value) {
      return;
    }
    isConfirming.value = true;

    try {
      await sentryService.withSpan(
        'face_selection_confirm',
        'ui.action',
        async () => {
          const selectedFaces = faceStore.facesSelected;

          track(
            '[Sentry] addBreadcrumb failed:',
            () => {
              sentryService.addBreadcrumb(
                'user',
                'Face selection confirm clicked',
                {
                  faces_selected: selectedFaces.length,
                  total_faces: faceStore.faces.length,
                }
              );
            },
            warnMessageOf
          );

          if (selectedFaces.length === 0) {
            track(
              '[Sentry] empty selection tracking failed:',
              () => {
                sentryService.addBreadcrumb(
                  'error',
                  'Face selection failed: no faces selected',
                  {},
                  'warning'
                );
                sentryService.captureMessage(
                  'Face selection: Empty selection submitted',
                  {
                    level: 'warning',
                    tags: { step: 'faces', issue: 'empty_selection' },
                    extra: { total_faces_available: faceStore.faces.length },
                  }
                );
              },
              warnMessageOf
            );
            ui.showError('face_selection_error_empty');
            throw new Error('No faces selected');
          }

          faceAnalytics.trackTimeTo(
            faceAnalytics.METRICS.time_face_selection_success
          );
          faceAnalytics.trackTimeTo(
            faceAnalytics.METRICS.time_demographics_confirmed
          );

          // The second argument is intentionally the legacy Analytics options slot.
          faceAnalytics.trackBoolean(
            faceAnalytics.METRICS.has_multiple_people,
            selectedFaces.length > 1
          );

          track(
            '[Sentry] face selection completion tracking failed:',
            () => {
              sentryService.setContext('face_selection', {
                total_faces_detected: faceStore.faces.length,
                faces_selected: selectedFaces.length,
                selection_complete: true,
              });
              sentryService.addBreadcrumb(
                'navigation',
                'Face selection completed successfully',
                {
                  faces_selected: selectedFaces.length,
                  is_multi_subject: selectedFaces.length >= 2,
                }
              );
            },
            warnMessageOf
          );

          faceRouter.redirectToStep('portraits');
        },
        {
          'faces.total_detected': faceStore.faces.length,
          'faces.selected_count': faceStore.facesSelected.length,
        }
      );
    } catch (error) {
      if ((error as Error).message !== 'No faces selected') {
        // biome-ignore lint/suspicious/noConsole: preserve kiosk support diagnostics.
        console.error('[Faces] Error during face selection confirm:', error);
        track(
          '[Sentry] captureException failed:',
          () => {
            sentryService.captureException(error, {
              tags: { step: 'faces', action: 'confirm_click' },
              extra: {
                total_faces: faceStore.faces.length,
                selected_count: faceStore.facesSelected.length,
              },
            });
          },
          warnMessageOf
        );
      }
    } finally {
      isConfirming.value = false;
    }
  }

  function leave(): void {
    isMounted.value = false;
    dropdownState.value = null;
    cancelPendingScroll();

    track(
      '[Faces] Failed to clear Sentry context:',
      () => {
        sentryService.setContext('face_selection', null);
      },
      warnMessageOf
    );
  }

  return {
    ageOptions,
    closeDropdown,
    demographicsConfirmations,
    dropdownOptions,
    dropdownPanelStyle,
    dropdownState,
    enter,
    genderOptions,
    getDropdownLabel,
    isConfirming,
    isFaceSelected,
    isMounted,
    leave,
    onAgeChange,
    onConfirmClick,
    onFaceClick,
    onGenderChange,
    onRaceChange,
    openDropdown,
    raceOptions,
    selectDropdownOption,
    showAgeSelector,
    showGenderSelector,
    showNonBinaryGender,
    showRaceSelector,
  };
}
